import { type ReactNode, useContext } from 'react'
import { Select } from '@base-ui/react/select'
import { ChevronDown } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { controlShellVariants, type ControlShellSize } from '../../control-shell'
import { type SelectItemType, SelectItemsContext, SelectMinWidthContext } from '../index'
import type { PartProps } from '../../../lib/part-props'

/**
 * Ground, edge, corner and every state are `controlShellVariants`, shared with Input and
 * Combobox.Input. What a trigger adds is the open state and the chevron's side, pulled in 2px
 * like an icon at a button's edge.
 */
const triggerClass = [
  'flex w-full min-w-0 items-center justify-between pr-[calc(var(--control-pad)-2px)]',
  '[&_[data-slot=select-placeholder]]:text-foreground-placeholder data-[popup-open]:border-hover',
  // Like a disabled Button: the not-allowed cursor shows, and the edge holds still on hover.
  'is-disabled:hover:border-disabled',
  // The placeholder and chevron set their own colour, so the disabled one has to reach them too.
  '[&_*]:is-disabled:text-foreground-disabled [&_[data-slot=select-placeholder]]:is-disabled:text-foreground-disabled',
].join(' ')

interface SelectTriggerProps extends PartProps<Select.Trigger.Props> {
  /** The shared field ramp: sm 24, md 28 (default), lg 32, xl 40. */
  size?: ControlShellSize
  className?: string
  items?: Array<SelectItemType>
  minWidth?: string
  hideIcon?: boolean
  placeholder?: ReactNode
}

function SelectTrigger({
  className,
  size,
  items: itemsOverride,
  minWidth: minWidthProp,
  hideIcon,
  placeholder,
  ...props
}: SelectTriggerProps) {
  const contextItems = useContext(SelectItemsContext)
  const contextMinWidth = useContext(SelectMinWidthContext)
  const items = itemsOverride || contextItems
  const minWidth = minWidthProp || contextMinWidth

  return (
    <Select.Trigger
      data-slot="select-trigger"
      className={twMerge(controlShellVariants({ size }), triggerClass, className)}
      style={minWidth ? { minWidth } : undefined}
      {...props}
    >
      <Select.Value>
        {(value: string | number | null) => {
          if (value == null && placeholder) return <span data-slot="select-placeholder">{placeholder}</span>
          const item = items?.find((item) => item.value === value)
          const displayText = item?.label || value
          return (
            // Same glyph and gap as Select.ItemText, so a value reads the same in the trigger and the list.
            <span className="flex items-center gap-1.5 [&_svg]:size-3.5">
              {item?.icon}
              {displayText}
            </span>
          )
        }}
      </Select.Value>
      {!hideIcon && (
        <Select.Icon className="text-foreground-secondary flex shrink-0">
          <ChevronDown className="size-3.5" />
        </Select.Icon>
      )}
    </Select.Trigger>
  )
}

export { SelectTrigger }
export type { SelectTriggerProps }
